import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, Eye, EyeOff, Building2 } from "lucide-react";

const CO_FIELDS = [
  ["company_name", "Company name", "e.g. WaterCo LLC"],
  ["address_line1", "Address line 1", "e.g. 575 Fifth Ave"],
  ["address_line2", "Address line 2", "e.g. New York, NY 10017"],
  ["bottler_name", "Bottler name", "Shown in the Bottler box on Production Orders"],
  ["gs1_company_prefix", "GS1 Company Prefix", "Digits only — required to generate SSCC pallet tags"],
  ["sscc_extension_digit", "SSCC Extension Digit", "Single digit 0-9 (defaults to 0)"],
];

function CompanyInfoForm() {
  const [co, setCo] = useState({ company_name: "", address_line1: "", address_line2: "", bottler_name: "", gs1_company_prefix: "", sscc_extension_digit: "" });
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(new Set());
  useEffect(() => {
    api.get("/company-settings").then(({ data }) => {
      setCo((p) => {
        const merged = { ...p, ...data };
        for (const k of dirtyRef.current) merged[k] = p[k];
        return merged;
      });
    }).catch(() => {});
  }, []);
  const edit = (k, v) => { dirtyRef.current.add(k); setCo((p) => ({ ...p, [k]: v })); };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/company-settings", co);
      toast.success("Company info saved — it will appear on Production Order documents");
    } catch {
      toast.error("Could not save company info");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="mt-8 max-w-md space-y-4 rounded-sm border border-slate-200 bg-white p-6" data-testid="company-info-form">
      <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.1em] text-slate-600">
        <Building2 className="h-4 w-4 text-[#002FA7]" /> Company info
      </div>
      <p className="text-xs text-slate-500">Shown in the header of generated Production Order documents.</p>
      {CO_FIELDS.map(([k, label, ph]) => (
        <div key={k} className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</Label>
          <Input value={co[k] || ""} onChange={(e) => edit(k, e.target.value)} placeholder={ph} className="h-9 rounded-sm" data-testid={`settings-co-${k}`} />
        </div>
      ))}
      <div className="flex justify-end">
        <Button type="submit" disabled={saving} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="settings-save-company">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save company info
        </Button>
      </div>
    </form>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — use it next time you sign in");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  const type = show ? "text" : "password";
  return (
    <main className="mx-auto max-w-[1100px] px-6 py-8 md:px-10 md:py-12" data-testid="settings-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Account</p>
      <h1 className="mt-1 text-4xl font-bold tracking-tighter text-slate-900 sm:text-5xl">Settings</h1>
      <p className="mt-2 text-sm text-slate-500">Signed in as {user?.email}</p>

      <CompanyInfoForm />

      <form onSubmit={submit} className="mt-8 max-w-md space-y-4 rounded-sm border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.1em] text-slate-600">
          <KeyRound className="h-4 w-4 text-[#002FA7]" /> Change password
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Current password</Label>
          <Input type={type} value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" className="h-9 rounded-sm" data-testid="settings-current-password" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">New password</Label>
          <Input type={type} value={next} onChange={(e) => setNext(e.target.value)} required autoComplete="new-password" className="h-9 rounded-sm" data-testid="settings-new-password" />
          <p className="text-[11px] text-slate-400">At least 8 characters. Mix letters, numbers and symbols for strength.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Confirm new password</Label>
          <Input type={type} value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" className="h-9 rounded-sm" data-testid="settings-confirm-password" />
        </div>
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => setShow((s) => !s)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800" data-testid="settings-toggle-visibility">
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {show ? "Hide" : "Show"} passwords
          </button>
          <Button type="submit" disabled={saving} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="settings-save-password">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
          </Button>
        </div>
      </form>
    </main>
  );
}
